import React, { useState, useEffect } from 'react';
import { GameCartridge, UserProfile } from '@/types';
import { CartridgeService } from '@/services/cartridgeService';
import { AIAgentService } from '@/services/aiAgentService';
import LoadingIndicator from '@/components/LoadingIndicator';

interface CartridgeBrowserScreenProps {
  onSelectCartridge: (cartridge: GameCartridge) => void;
  onBack: () => void;
  onCreateCartridge: () => void;
}

const CartridgeBrowserScreen: React.FC<CartridgeBrowserScreenProps> = ({
  onSelectCartridge,
  onBack,
  onCreateCartridge
}) => {
  const [cartridges, setCartridges] = useState<GameCartridge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('');

  useEffect(() => {
    loadCartridges();
  }, []);

  const loadCartridges = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[CartridgeBrowserScreen] Loading available cartridges...');
      const availableCartridges = await CartridgeService.getAvailableCartridges();
      console.log('[CartridgeBrowserScreen] Loaded cartridges:', availableCartridges.length, availableCartridges);
      setCartridges(availableCartridges);
    } catch (err) {
      console.error('Error loading cartridges:', err);
      let errorMessage = 'Failed to load cartridges. ';
      if (err instanceof Error) {
        if (err.message.includes('network') || err.message.includes('Network')) {
          errorMessage += 'Please check your internet connection.';
        } else if (err.message.includes('permission') || err.message.includes('401')) {
          errorMessage += 'You may need to sign in again.';
        } else {
          errorMessage += 'Please try refreshing the page.';
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCartridges = cartridges.filter(cartridge => {
    const matchesSearch = searchQuery === '' || 
      cartridge.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cartridge.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cartridge.author.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTag = selectedTag === '' || cartridge.tags.includes(selectedTag);
    
    return matchesSearch && matchesTag;
  });

  // Get all unique tags for filtering
  const allTags = Array.from(new Set(cartridges.flatMap(c => c.tags))).sort();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <LoadingIndicator message="Loading cartridges..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-orbitron text-cyan-400 neon-text-cyan mb-2 animate-slide-in floating">
              Cartridge Library
            </h1>
            <p className="text-slate-400">
              Choose your adventure from available game cartridges
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={onCreateCartridge}
              className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors font-semibold cyber-button neon-border"
            >
              + Create New Cartridge
            </button>
            <button
              onClick={onBack}
              className="px-6 py-3 glassmorphism-strong hover:bg-slate-600 text-white rounded-lg transition-colors cyber-button"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search cartridges..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
              />
            </div>
            <div className="sm:w-48">
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
              >
                <option value="">All Categories</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-900/50 border border-red-500/50 rounded-lg text-red-200 text-center mb-8">
            <p className="font-semibold">Error loading cartridges:</p>
            <p>{error}</p>
            <button 
              onClick={loadCartridges} 
              className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Cartridge Grid */}
        {filteredCartridges.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCartridges.map(cartridge => (
              <CartridgeCard 
                key={cartridge.id} 
                cartridge={cartridge} 
                onSelect={() => onSelectCartridge(cartridge)} 
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-2xl text-slate-500 font-semibold mb-2">
              No Cartridges Found
            </p>
            <p className="text-slate-400">
              {searchQuery || selectedTag 
                ? 'Try adjusting your search or filters'
                : 'No cartridges available'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Individual cartridge card component
interface CartridgeCardProps {
  cartridge: GameCartridge;
  onSelect: () => void;
}

const CartridgeCard: React.FC<CartridgeCardProps> = ({ cartridge, onSelect }) => {
  console.log('[CartridgeCard] Rendering cartridge:', cartridge.name, 'thumbnailUrl:', cartridge.thumbnailUrl);
  
  const getTagColor = (tag: string) => {
    const colors = {
      'Cyberpunk': 'bg-cyan-500/20 text-cyan-400',
      'Fantasy': 'bg-emerald-500/20 text-emerald-400',
      'Sci-Fi': 'bg-blue-500/20 text-blue-400',
      'Philosophy': 'bg-purple-500/20 text-purple-400',
      'AI': 'bg-pink-500/20 text-pink-400',
      'Dreams': 'bg-indigo-500/20 text-indigo-400',
      'Magic': 'bg-orange-500/20 text-orange-400',
      'Adventure': 'bg-green-500/20 text-green-400',
    };
    return colors[tag as keyof typeof colors] || 'bg-slate-500/20 text-slate-400';
  };

  return (
    <div className="glassmorphism border border-slate-700 rounded-lg overflow-hidden hover:border-cyan-400/50 transition-all group cyber-button animate-fade-in-scale">
      {/* Thumbnail */}
      <div className="aspect-video bg-slate-900 flex items-center justify-center overflow-hidden">
        {cartridge.thumbnailUrl ? (
          <img 
            src={cartridge.thumbnailUrl} 
            alt={cartridge.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="text-4xl">🎮</div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-semibold text-white group-hover:text-cyan-400 transition-colors">
            {cartridge.name}
          </h3>
          <span className="text-xs text-slate-400 bg-slate-700 px-2 py-1 rounded">
            v{cartridge.version}
          </span>
        </div>

        <p className="text-slate-300 text-sm mb-4 line-clamp-3">
          {cartridge.description}
        </p>

        {/* AI Agents Preview */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-cyan-400">AI COMPANIONS</span>
            <div className="h-px bg-cyan-400/30 flex-1"></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <span className="text-xs text-cyan-400">J</span>
            </div>
            <span className="text-xs text-slate-400">JAX</span>
            <div className="w-6 h-6 rounded-full bg-pink-500/20 flex items-center justify-center">
              <span className="text-xs text-pink-400">L</span>
            </div>
            <span className="text-xs text-slate-400">LYRA</span>
            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
              <span className="text-xs text-purple-400">E</span>
            </div>
            <span className="text-xs text-slate-400">ECHO</span>
          </div>
        </div>

        {/* Features */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Voice Chat</span>
            <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">Karma System</span>
            <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">Multiplayer</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-slate-400 mb-4">
          <span>by {cartridge.author}</span>
          <span>{cartridge.maxPlayers} players max</span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {cartridge.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              className={`px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}
            >
              {tag}
            </span>
          ))}
          {cartridge.tags.length > 3 && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-600/20 text-slate-400">
              +{cartridge.tags.length - 3}
            </span>
          )}
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-1 mb-4">
          {cartridge.supportedFeatures?.map(feature => (
            <span
              key={feature}
              className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300"
              title={feature}
            >
              {feature === 'multiplayer' && '👥'}
              {feature === 'voice' && '🎤'}
              {feature === 'images' && '🖼️'}
              {feature === 'saves' && '💾'}
              {feature === 'custom_companions' && '🤖'}
              {feature === 'branching_story' && '🌳'}
            </span>
          )) || (
            <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-400">
              No features listed
            </span>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={() => {
            console.log('[CartridgeCard] Select button clicked for:', cartridge.name);
            onSelect();
          }}
          className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-cyan-400 cyber-button neon-border"
        >
          Select Cartridge
        </button>
      </div>
    </div>
  );
};

export default CartridgeBrowserScreen;
